import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../api';
import { useAuth } from '../auth';
import { setLanguage } from '../i18n';

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Nach dem Login zur ursprünglich angefragten Seite (z. B. geteilter /join-Link).
  const redirectTo = location.pathname !== '/' ? location.pathname + location.search : '/';
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const lang = i18n.language.startsWith('en') ? 'en' : 'de';
      const res =
        mode === 'login'
          ? await api.auth.login(email, password)
          : await api.auth.register({ email, password, displayName, inviteCode: inviteCode || undefined, language: lang });
      setUser(res.user);
      setLanguage(res.user.language);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const code = err instanceof ApiError ? err.code : 'generic';
      setError(t([`errors.${code}`, 'errors.generic']));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="container">
      <div className="auth-wrap stack">
        <div className="center stack" style={{ gap: 6, marginBottom: 8 }}>
          <div className="brand" style={{ justifyContent: 'center' }}>
            <span className="brand-badge">FWR</span>
            <span className="name" style={{ fontSize: '1.1rem' }}>{t('common.appName')}</span>
          </div>
          <p className="muted" style={{ margin: 0 }}>{t('auth.welcome')}</p>
        </div>

        <div className="card stack">
          <div className="tabs">
            <button className={mode === 'login' ? 'active' : ''} onClick={() => setMode('login')}>
              {t('auth.login')}
            </button>
            <button className={mode === 'register' ? 'active' : ''} onClick={() => setMode('register')}>
              {t('auth.register')}
            </button>
          </div>

          <form className="stack" onSubmit={submit}>
            {mode === 'register' && (
              <div className="field">
                <label htmlFor="dn">{t('auth.displayName')}</label>
                <input id="dn" value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
              </div>
            )}
            <div className="field">
              <label htmlFor="em">{t('auth.email')}</label>
              <input id="em" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="field">
              <label htmlFor="pw">{t('auth.password')}</label>
              <input
                id="pw"
                type="password"
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              {mode === 'register' && <span className="hint">{t('auth.passwordHint')}</span>}
            </div>
            {mode === 'register' && (
              <div className="field">
                <label htmlFor="iv">{t('auth.inviteCode')}</label>
                <input id="iv" value={inviteCode} onChange={(e) => setInviteCode(e.target.value)} />
                <span className="hint">{t('auth.inviteHint')} {t('auth.firstUserAdmin')}</span>
              </div>
            )}

            {error && <div className="notice notice-error">{error}</div>}

            <button className="btn btn-primary" type="submit" disabled={busy} data-testid="auth-submit">
              {busy ? t('common.loading') : mode === 'login' ? t('auth.login') : t('auth.register')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
